/* =========================================================================
 * CLEAR THIS CROWN — the control, on the record, from a phone.
 *
 * Brian: correcting a bad crown should be a control in the Command Center —
 * one authenticated call, triggerable from a phone, no terminal, no sandbox,
 * nothing living on his machine. Four wrong champions sat live for a day
 * because clearing one meant dispatching a shell run.
 *
 * IT LIVES IN THE DRAWER because that is where somebody REALISES it is wrong:
 * the record is open, the champion is on screen, and the missing source is
 * visible right above this. The row already flags "no source" — this is the
 * thing to do about it.
 *
 * ⚠️ THE COPY DOES NOT SAY DELETE, AND THAT IS NOT SOFTENING.
 *
 * What this does is WITHDRAW AN UNSUPPORTED CLAIM. It restores the record to
 * its real state — a championship with no champion established — narrates the
 * withdrawal onto the record and files a ledger row per field. Both misreadings
 * cost something: a rep who thinks they are destroying data will not press it
 * and the bad crown stays live, and a rep who thinks it is harmless will press
 * it carelessly on a crown that was fine. So the panel says what goes, what
 * stays, and what the record will read afterwards.
 *
 * ⚠️ THE PERMISSION IS SYSTEM_RUN, AND THAT IS DELIBERATELY NOT WHAT THE
 * CORRECTION TIER WOULD SUGGEST.
 *
 * The consequence — removing a champion from a live record — reads like
 * CC_CHAMPION_UPDATE, which is exec + rep and would let the person working the
 * book fix their own record. But `sr-portal-backend/permissions.js` maps
 * `POST /api/championships/repair-defects` to SYSTEM_RUN, which is exec + ops,
 * and the client gate mirrors the ROUTE rather than the intent. Gating this
 * wider than the server would draw a live button for every rep that answers 403
 * on every press — a control that looks like the fix and is not one, which is
 * worse than no control at all. The consequence of that choice is real and
 * named in the report: a rep cannot clear a bad crown on their own book.
 *
 * This is a DRAWING decision only (hard rule 5). The route re-decides it from
 * the verified JWT, and the predicates decide the rest.
 * ========================================================================= */

import { useState } from 'react'
import {
  canClearCrown, clearEligibility, clearPatch, CLEARS, KEEPS, CLEAR_CAP,
} from './clearCrown.js'
import { clearCrownRun } from './clearCrownRun.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import styles from './pursuit.module.css'

export { canClearCrown }

export default function ClearCrown({ record, onCleared, readDegraded = false }) {
  const { can, why } = useAccess()
  const [stage, setStage] = useState('idle') // idle | confirm | working | done
  const [verdict, setVerdict] = useState(null)
  const [round, setRound] = useState(0)

  if (!canClearCrown(record)) return null

  const elig = clearEligibility(record, { readDegraded })
  const mayRun = can(ACTIONS.SYSTEM_RUN, record)

  const run = async () => {
    setStage('working'); setVerdict(null); setRound(0)
    try {
      const v = await clearCrownRun(record, { onProgress: ({ round: r }) => setRound(r) })
      setVerdict(v)
      setStage('done')
      /* ⚠️ ONLY A CONFIRMED WRITE REPAINTS THE BOARD, and only with the fields
       * the response named. Patching on anything else would put "no champion"
       * on a row whose champion is still in HubSpot — the two-sources-of-truth
       * fault this board has been bitten by before. */
      if (v.kind === 'cleared') {
        const patch = clearPatch(v.fields)
        if (patch) onCleared && onCleared(String(record.id), patch)
      }
    } catch (err) {
      /* A thrown error is a transport or authorization failure — 403 from the
       * route re-deciding, or the network. The server's own words, because
       * "failed" is not actionable and these messages are. */
      setVerdict({
        kind: 'error',
        message: err?.body?.detail || err?.body?.error || err?.message || 'The correction did not run.',
        hint: 'Nothing was written.',
      })
      setStage('done')
    }
  }

  /* ---- NOT AVAILABLE, AND ALWAYS WITH THE REASON -----------------------
   * Never hidden. A rep who cannot see why a control is unavailable concludes
   * the feature is broken and asks for it to be fixed — the same reading as a
   * person with no email in OutreachPicker. */
  if (!mayRun) {
    return (
      <div className={styles.clearCrown}>
        <div className={styles.clearHead}>Clear this crown</div>
        <div className={styles.clearBlocked}>
          {why(ACTIONS.SYSTEM_RUN, record)
            || 'Withdrawing a champion is not available for your role. It runs as a system correction, '
              + 'so it sits with exec and ops rather than with the rep book.'}
        </div>
      </div>
    )
  }

  if (!elig.ok && stage === 'idle') {
    return (
      <div className={styles.clearCrown}>
        <div className={styles.clearHead}>Clear this crown</div>
        <div className={styles.clearBlocked}>{elig.reason}</div>
      </div>
    )
  }

  return (
    <div className={styles.clearCrown}>
      <div className={styles.clearHead}>Clear this crown</div>

      {/* ⚠️ THE REASON COMES BEFORE THE BUTTON, ALWAYS. "No source on file" is
          the entire justification for the action. An affordance offered with no
          reason above it is how a good crown gets withdrawn by somebody who
          misread the row. */}
      {stage !== 'done' && <div className={styles.clearWhy}>{elig.reason}</div>}

      {stage === 'idle' && (
        <button type="button" className={styles.clearOpen} onClick={() => setStage('confirm')}>
          Withdraw this claim…
        </button>
      )}

      {/* ⚠️ A DELIBERATE GESTURE, NOT A TAP — and the champion is NAMED in it.
          Propose-don't-overwrite is the standing rule; this is a human
          deliberately overriding a recorded value, so it takes a second,
          specific act against the actual name. */}
      {stage === 'confirm' && (
        <div className={styles.clearConfirm}>
          <p className={styles.clearAsk}>
            Withdraw <b>“{elig.champion}”</b> as champion of this record?
          </p>
          <p className={styles.clearDetail}>
            This clears {CLEARS.join(', ')}. It keeps {KEEPS}. The record will read as having
            no champion established, and it will carry a note saying the claim was withdrawn for
            having no source — so nobody re-crowns it from the same guess. Every old value stays
            in the ledger.
          </p>
          <div className={styles.clearActions}>
            <button type="button" className={styles.clearGo} onClick={run}>
              Yes — withdraw “{elig.champion}”
            </button>
            <button type="button" className={styles.clearCancel} onClick={() => setStage('idle')}>
              Keep it
            </button>
          </div>
        </div>
      )}

      {stage === 'working' && (
        <div className={styles.clearWorking}>
          {/* The walk is the slow part and saying so beats a spinner that looks
              stuck. See the page-walk note in clearCrown.js. */}
          Reading the championship object to find this record{round > 1 ? ` — pass ${round}` : ''}…
        </div>
      )}

      {stage === 'done' && verdict && <Verdict v={verdict} />}
    </div>
  )
}

/* ---- WHAT THE SERVER ACTUALLY SAID -------------------------------------
 *
 * ⚠️ EVERY ONE OF THESE ARRIVES AS HTTP 200. Cap exceeded, degraded read, a
 * record that did not match, a walk that did not reach it — all successful
 * responses carrying `written: 0`. A refusal is a FINDING and is rendered as
 * one; only `cleared` is drawn as a yes, and only `readClearResult` decides
 * which is which.
 */
function Verdict({ v }) {
  if (v.kind === 'cleared') {
    return (
      <div className={styles.clearOk}>
        <b>Withdrawn.</b> {v.message}
        {v.unlogged?.length > 0 && (
          /* ⚠️ WROTE BUT DID NOT LOG IS ITS OWN OUTCOME. The change landed and
             the audit row did not — worse than either a clean success or a
             clean refusal, and it cannot be rolled back, so it is said. */
          <div className={styles.clearWarn}>
            The change landed but its audit row did not: {v.unlogged[0]}
          </div>
        )}
      </div>
    )
  }

  if (v.kind === 'cap') {
    return (
      <div className={styles.clearRefused}>
        <b>Refused — and it is worth reading.</b>
        <div>{v.message}</div>
        {v.census?.upperBound != null && (
          <div className={styles.clearCensus}>
            {v.census.upperBound} records are crowned with no source; the cap is {v.census.cap ?? CLEAR_CAP}.
          </div>
        )}
        <div className={styles.clearHint}>{v.hint}</div>
      </div>
    )
  }

  return (
    <div className={v.kind === 'error' ? styles.clearErr : styles.clearRefused}>
      <b>{v.kind === 'nomatch' ? 'Not withdrawn.' : v.kind === 'error' ? 'Did not run.' : 'Refused.'}</b>
      <div>{v.message}</div>
      {v.hint && <div className={styles.clearHint}>{v.hint}</div>}
    </div>
  )
}
